import React, { useMemo } from 'react';
import { formatDate } from '../../../config/appConfig';
import {
  ReportBody,
  ReportCheck,
  ReportField,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportPack,
  ReportPage,
  ReportTitleRow,
} from '../../../components/ui/Report';

export type RestRecordInterval = {
  startMinute: number;
  endMinute: number;
};

export type RestRecordDayCell = {
  workHours: number;
  restHours: number;
  workHours7d: number;
  restHours7d: number;
  notes: string | null;
  compliant?: boolean;
  violationCodes?: string[];
  intervals: RestRecordInterval[];
};

export type RestRecordCrew = {
  crewId: number;
  crewName: string;
  rankName?: string | null;
  watchkeeper: boolean;
  days: Record<string, RestRecordDayCell | undefined>;
};

export type RestRecordSheet = {
  vesselId: number;
  vesselName: string;
  vesselAbrv?: string | null;
  imoNumber?: string | null;
  flag?: { id: number; name: string; code?: string | null } | null;
  managementCompany?: {
    id: number;
    name: string;
    code?: string | null;
    logoStorageKey?: string | null;
    logoOriginalFilename?: string | null;
  } | null;
  month: string;
  dates: string[];
  rules?: {
    name?: string;
    minRest24h?: number;
    minRest7d?: number;
    maxWork24h?: number;
    notes?: string | null;
  } | null;
  crew: RestRecordCrew[];
};

const DAY_MIN = 24 * 60;
const SLOT = 30;
const SLOT_COUNT = DAY_MIN / SLOT;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatHours(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function monthYearLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function isRestSlot(work: RestRecordInterval[], slotStart: number): boolean {
  const slotEnd = slotStart + SLOT;
  for (const iv of work) {
    const start = Math.max(0, iv.startMinute);
    const end = Math.min(DAY_MIN, iv.endMinute > DAY_MIN ? DAY_MIN : iv.endMinute);
    if (end <= start) continue;
    if (start < slotEnd && end > slotStart) return false;
  }
  return true;
}

function dayRestMask(intervals: RestRecordInterval[] | undefined): boolean[] {
  const work = intervals ?? [];
  const mask: boolean[] = [];
  for (let i = 0; i < SLOT_COUNT; i += 1) {
    mask.push(isRestSlot(work, i * SLOT));
  }
  return mask;
}

type RestRecordReportProps = {
  data: RestRecordSheet;
  crewId?: number | null;
  /** When true, tint non-compliant day rows (screen + print). */
  annotateCompliance?: boolean;
  /** Management company logo (authenticated blob URL). */
  companyLogoSrc?: string | null;
};

function CoverField({
  label,
  value,
  grow,
  children,
}: {
  label: React.ReactNode;
  value?: React.ReactNode;
  grow?: number;
  children?: React.ReactNode;
}) {
  return (
    <div
      className="wrh-rr-cover-field"
      style={grow != null ? { flex: grow } : undefined}
    >
      <span className="wrh-rr-cover-field-label">{label}</span>
      <span className="wrh-rr-cover-field-line">{children ?? value ?? ''}</span>
    </div>
  );
}

function CoverCheck({ label, checked }: { label: string; checked: boolean }) {
  return (
    <span className={checked ? 'wrh-rr-cover-check on' : 'wrh-rr-cover-check'}>
      {label} {checked ? '☑' : '☐'}
    </span>
  );
}

function RestRecordIdentity({
  vesselTitle,
  imoNumber,
  flagLabel,
  crew,
  monthLabel,
  companyLogoSrc,
  companyName,
  formId,
  marking,
}: {
  vesselTitle: string;
  imoNumber?: string | null;
  flagLabel: string;
  crew: RestRecordCrew;
  monthLabel: string;
  companyLogoSrc?: string | null;
  companyName?: string | null;
  formId: string;
  marking?: string;
}) {
  return (
    <ReportHeader>
      <ReportTitleRow
        title="Record of hours of rest of seafarers"
        formId={formId}
        logoSrc={companyLogoSrc}
        logoAlt={companyName || 'Management company logo'}
      />
      <ReportMeta>
        <ReportMetaRow>
          <ReportField label="Name of ship" value={vesselTitle} />
          <ReportField label="IMO number (if any)" value={imoNumber || '—'} />
          <ReportField label="Flag of ship" value={flagLabel} />
        </ReportMetaRow>
        <ReportMetaRow>
          <ReportField label="Seafarer (full name)" value={crew.crewName} />
          <ReportField label="Position / rank" value={crew.rankName || '—'} />
          <ReportField label="Month and year" value={monthLabel} />
        </ReportMetaRow>
        <ReportMetaRow>
          <ReportField label="Watchkeeper" colSpan={marking ? 2 : 3}>
            <ReportCheck label="yes" checked={crew.watchkeeper} />
            <ReportCheck label="no" checked={!crew.watchkeeper} />
          </ReportField>
          {marking ? <ReportField label="Marking" value={marking} /> : null}
        </ReportMetaRow>
      </ReportMeta>
    </ReportHeader>
  );
}

export const RestRecordReport: React.FC<RestRecordReportProps> = ({
  data,
  crewId,
  annotateCompliance = false,
  companyLogoSrc = null,
}) => {
  const crewList = useMemo(() => {
    if (crewId == null) return data.crew;
    return data.crew.filter((c) => c.crewId === crewId);
  }, [data.crew, crewId]);

  const vesselTitle = `${data.vesselName}${data.vesselAbrv ? ` (${data.vesselAbrv})` : ''}`;
  const monthLabel = monthYearLabel(data.month);
  const flagLabel = data.flag?.name || data.flag?.code || '—';
  const rules = data.rules;
  const lawsLine = rules?.notes?.trim()
    ? rules.notes.trim()
    : 'MLC, 2006 and STCW 1978 Convention, as amended (company WRH rule set'
      + (rules?.name ? `: ${rules.name}` : '')
      + ').';
  const identity = {
    vesselTitle,
    imoNumber: data.imoNumber,
    flagLabel,
    monthLabel,
    companyLogoSrc,
    companyName: data.managementCompany?.name,
  };

  const pages = crewList.flatMap((crew, idx) => [
    <ReportPage
      key={`${crew.crewId}-cover`}
      size="landscape"
      breakAfter
      className="ui-report-page-filled wrh-rr-cover-page"
      pageNumber={1}
      pageCount={2}
      showFooter={false}
    >
      <header className="wrh-rr-cover-head">
        <div className="wrh-rr-cover-banner">
          <div className="wrh-rr-cover-banner-side">
            {identity.companyLogoSrc ? (
              <img
                src={identity.companyLogoSrc}
                alt={identity.companyName || 'Management company logo'}
                className="wrh-rr-cover-logo"
              />
            ) : null}
          </div>
          <h1 className="wrh-rr-cover-title">
            Model format for record of hours of work or hours of rest of seafarers
            <sup>1</sup>
          </h1>
          <div className="wrh-rr-cover-banner-side wrh-rr-cover-pageno">Page 1 of 2</div>
        </div>
        <div className="wrh-rr-cover-row">
          <CoverField grow={1.6} label="Name of ship:" value={identity.vesselTitle} />
          <CoverField grow={1} label="IMO number (if any):" value={identity.imoNumber || ''} />
          <CoverField
            grow={1}
            label="Flag of ship:"
            value={identity.flagLabel === '—' ? '' : identity.flagLabel}
          />
        </div>
        <div className="wrh-rr-cover-row">
          <CoverField grow={1.2} label="Seafarer (full name):" value={crew.crewName} />
          <CoverField grow={1} label="Position / rank:" value={crew.rankName || ''} />
        </div>
        <div className="wrh-rr-cover-row">
          <CoverField grow={0.9} label="Month and year:" value={identity.monthLabel} />
          <div className="wrh-rr-cover-field wrh-rr-cover-watch">
            <span className="wrh-rr-cover-field-label">
              Watchkeeper:<sup>2</sup>
            </span>
            <CoverCheck label="yes" checked={crew.watchkeeper} />
            <CoverCheck label="no" checked={!crew.watchkeeper} />
          </div>
        </div>
      </header>
      <ReportBody className="wrh-rr-cover-body">
        <h2 className="wrh-rr-cover-heading">
          Record of hours of work/rest<sup>3</sup>
        </h2>
        <p className="wrh-rr-cover-hint">
          Please mark periods of work or rest, as applicable, with an X, or using a continuous
          line or arrow.
        </p>
        <div className="wrh-rr-cover-reverse" role="note">
          COMPLETE THE TABLE ON THE REVERSE SIDE
        </div>
        <p className="wrh-rr-cover-laws">
          The following national laws, regulations and/or collective agreements governing
          limitations on working hours or minimum rest periods apply to this ship:
        </p>
        <p className="wrh-rr-cover-laws-line">{lawsLine}</p>
      </ReportBody>
      <footer className="wrh-rr-cover-foot">
        <p className="wrh-rr-cover-agree">
          I agree that this record is an accurate reflection of the hours of work or rest of the
          seafarer concerned.
        </p>
        <div className="wrh-rr-cover-sign wrh-rr-cover-sign-full">
          <span className="wrh-rr-cover-sign-label">
            Name of master or person authorized by master to sign this record
          </span>
          <span className="wrh-rr-cover-sign-line" aria-hidden />
        </div>
        <div className="wrh-rr-cover-signs">
          <div className="wrh-rr-cover-sign">
            <span className="wrh-rr-cover-sign-label">Signature of master or authorized person</span>
            <span className="wrh-rr-cover-sign-line" aria-hidden />
          </div>
          <div className="wrh-rr-cover-sign">
            <span className="wrh-rr-cover-sign-label">Signature of seafarer</span>
            <span className="wrh-rr-cover-sign-line" aria-hidden />
          </div>
        </div>
        <p className="wrh-rr-cover-copy">
          A copy of this record is to be given to the seafarer. This form is subject to examination
          and endorsement under procedures established by
        </p>
        <p className="wrh-rr-cover-authority-line" aria-hidden />
        <p className="wrh-rr-cover-authority-caption">(name of competent authority)</p>
        <hr className="wrh-rr-cover-rule" />
        <p className="wrh-rr-cover-fn">
          <sup>1</sup> The terms used in this model table are to appear in the working language or
          languages of the ship and in English.
        </p>
        <p className="wrh-rr-cover-fn">
          <sup>2</sup> Check ✓ as appropriate.
        </p>
        <p className="wrh-rr-cover-fn">
          <sup>3</sup> Delete as appropriate.
        </p>
      </footer>
    </ReportPage>,
    <ReportPage
      key={`${crew.crewId}-grid`}
      size="landscape"
      breakAfter={idx < crewList.length - 1}
      className="ui-report-page-filled wrh-rr-grid-page"
      pageNumber={2}
      pageCount={2}
    >
      <RestRecordIdentity
        {...identity}
        crew={crew}
        formId="IMO / ILO Appendix 4 · Page 2 of 2"
        marking="Rest periods marked ■ · work left blank"
      />
      <ReportBody>
        <table className="wrh-rr-grid">
          <thead>
            <tr>
              <th rowSpan={2} className="wrh-rr-date">
                Date
              </th>
              <th colSpan={SLOT_COUNT} className="wrh-rr-hours-head">
                Hours — mark periods of rest
              </th>
              <th rowSpan={2} className="wrh-rr-num">
                Hours of rest in 24-hour period
              </th>
              <th rowSpan={2} className="wrh-rr-comments">
                Comments
              </th>
              <th colSpan={2} className="wrh-rr-office">
                Not to be completed by the seafarer<sup>1</sup>
              </th>
            </tr>
            <tr>
              {Array.from({ length: 24 }, (_, h) => (
                <th key={h} colSpan={2} className="wrh-rr-hour">
                  {pad2(h)}
                </th>
              ))}
              <th className="wrh-rr-num wrh-rr-office">
                Hours of work / rest in any 24-hour period<sup>2</sup>
              </th>
              <th className="wrh-rr-num wrh-rr-office">
                Hours of work / rest in any 7-day period<sup>2</sup>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.dates.map((date) => {
              const cell = crew.days[date];
              const mask = dayRestMask(cell?.intervals);
              const rest24 = cell?.restHours;
              const work24 = cell?.workHours;
              const rest7 = cell?.restHours7d;
              const work7 = cell?.workHours7d;
              const nonCompliant =
                annotateCompliance && cell != null && cell.compliant === false;
              return (
                <tr
                  key={date}
                  className={nonCompliant ? 'wrh-rr-row-nc' : undefined}
                  title={
                    nonCompliant
                      ? (cell?.violationCodes?.length
                          ? `Non-compliant: ${cell.violationCodes.join(', ')}`
                          : 'Non-compliant')
                      : undefined
                  }
                >
                  <td className="wrh-rr-date">
                    {formatDate(date)}
                    {nonCompliant ? (
                      <span className="wrh-rr-nc-mark" aria-label="Non-compliant">
                        !
                      </span>
                    ) : null}
                  </td>
                  {mask.map((rest, i) => (
                    <td
                      key={i}
                      className={rest ? 'wrh-rr-slot rest' : 'wrh-rr-slot work'}
                      title={`${pad2(Math.floor((i * SLOT) / 60))}:${pad2((i * SLOT) % 60)}`}
                    >
                      {rest ? '■' : ''}
                    </td>
                  ))}
                  <td className="wrh-rr-num">{formatHours(rest24)}</td>
                  <td className="wrh-rr-comments">
                    {cell?.notes?.trim() || ''}
                    {nonCompliant && cell?.violationCodes?.length
                      ? `${cell.notes?.trim() ? ' · ' : ''}${cell.violationCodes.join(', ')}`
                      : ''}
                  </td>
                  <td className="wrh-rr-num wrh-rr-office">
                    {cell
                      ? `W ${formatHours(work24)} / R ${formatHours(rest24)}`
                      : '—'}
                  </td>
                  <td className="wrh-rr-num wrh-rr-office">
                    {cell
                      ? `W ${formatHours(work7)} / R ${formatHours(rest7)}`
                      : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ReportBody>
      <footer className="wrh-rr-grid-foot">
        <p className="wrh-rr-grid-fn">
          <sup>1</sup> For completion and use in accordance with the procedures established by the
          competent authority in compliance with the relevant requirements of the Seafarers’
          Hours of Work and the Manning of Ships Convention, 1996 (Convention No. 180).
        </p>
        <p className="wrh-rr-grid-fn">
          <sup>2</sup> Additional calculations or verifications may be necessary to ensure
          compliance with the relevant requirements of the Seafarers’ Hours of Work and the
          Manning of Ships Convention, 1996 (Convention No. 180) and the International Convention
          on Standards of Training, Certification and Watchkeeping, 1978, as amended (STCW
          Convention).
        </p>
      </footer>
    </ReportPage>,
  ]);

  return <ReportPack>{pages}</ReportPack>;
};

export default RestRecordReport;
